import { useSyncExternalStore } from "react";

/**
 * True below the auth surface's 900px breakpoint — the point where the shell
 * drops the proof column and the fields go placeholder-only (vb-auth.css).
 *
 * Exists because the prototype swaps `placeholder` text at that width, which
 * CSS cannot do. Everything else about the phone layout is handled in CSS.
 *
 * NOT `useIsMobile` — that fires at MOBILE_BREAKPOINT (640px). This value must
 * stay identical to the `@media (max-width: 900px)` block in vb-auth.css: the
 * CSS hides the field label and the JS supplies the label-bearing placeholder,
 * so any drift leaves 641–900px with neither a visible label nor a
 * label-bearing placeholder. Change the two together or not at all.
 */
const QUERY = "(max-width: 900px)";

/**
 * One MediaQueryList for the whole surface, created on first use.
 *
 * Lazy rather than module-scope because this module is imported by tests that
 * render to static markup in Node, where `window` does not exist. Shared rather
 * than per-hook because `getSnapshot` runs on every render, and create-account
 * mounts four fields — a fresh `matchMedia` per field per render, plus four
 * separate listeners, for a value that is identical everywhere.
 */
let mediaQuery: MediaQueryList | null = null;
const getMediaQuery = () => (mediaQuery ??= window.matchMedia(QUERY));

const subscribe = (onChange: () => void) => {
    const mq = getMediaQuery();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
};

const getSnapshot = () => getMediaQuery().matches;
// SSR/prerender: assume desktop. The CSS corrects the layout on hydrate.
const getServerSnapshot = () => false;

export function usePhoneLayout(): boolean {
    return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
